import dayjs from 'dayjs';
import {
    normalizeWorkoutRecord,
    WORKOUT_EXERCISE_MARKER,
    WorkoutExercise,
} from '../../lib/workoutRecord';
import WorkoutRecordView, {
    WorkoutClosingBlock,
    WorkoutSummaryBlock,
} from '../Feed/WorkoutRecord/WorkoutRecordView';
import WorkoutExerciseCard from '../Feed/WorkoutRecord/WorkoutExerciseCard';

type Props = {
    journal?: any | null;
    isLoading?: boolean;
    isError?: boolean;
    error?: any;
    displayName?: string;
    coachName?: string;
    startTime?: string;
    endTime?: string;
    onRetry?: () => void;
};

export const getNewOpportunityJournalErrorMessage = (error?: any) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403 || status === 406) {
        return '이 운동일지를 조회할 권한이 없습니다.';
    }
    if (status === 409) {
        return '운동일지 연결 정보가 변경되었습니다. 최신 정보로 다시 조회해주세요.';
    }
    if (status === 404) {
        return '신규기회 또는 운동일지를 찾을 수 없습니다.';
    }
    return '운동일지를 불러오지 못했습니다. 네트워크 상태를 확인해주세요.';
};

const NewOpportunityJournalReadOnly = ({
    journal,
    isLoading,
    isError,
    error,
    displayName,
    coachName,
    startTime: opportunityStartTime,
    endTime: opportunityEndTime,
    onRetry,
}: Props) => {
    if (isLoading) {
        return (
            <div style={styles.empty} role="status" aria-live="polite">
                운동일지를 불러오는 중입니다.
            </div>
        );
    }

    if (isError) {
        return (
            <div style={styles.error} role="alert">
                <span>{getNewOpportunityJournalErrorMessage(error)}</span>
                {onRetry && (
                    <button type="button" style={styles.retryButton} onClick={onRetry}>
                        다시 시도
                    </button>
                )}
            </div>
        );
    }

    if (!journal) {
        return (
            <div style={styles.empty} role="status">
                작성된 운동일지가 없습니다.
            </div>
        );
    }

    const scheduleInfo = journal?.scheduleReport?.scheduleInfo || {};
    const title = scheduleInfo.sessionTitle || displayName || '수업';
    const workoutRecord = normalizeWorkoutRecord(journal?.scheduleReport?.workoutRecord);
    const writerName =
        journal?.writerName || journal?.profileName || scheduleInfo?.coachName || coachName;
    const startTime =
        (scheduleInfo.startTime || opportunityStartTime) &&
        dayjs(scheduleInfo.startTime || opportunityStartTime).isValid()
            ? dayjs(scheduleInfo.startTime || opportunityStartTime)
            : null;
    const endTime =
        (scheduleInfo.endTime || opportunityEndTime) &&
        dayjs(scheduleInfo.endTime || opportunityEndTime).isValid()
            ? dayjs(scheduleInfo.endTime || opportunityEndTime)
            : null;
    const contents = Array.isArray(journal?.contents) ? journal.contents : [];
    const contentTypeOf = (content: any) =>
        String(content?.contentType || content?.type || '').toUpperCase();
    const hasWorkoutMarkers =
        !!workoutRecord &&
        contents.some((content: any) => contentTypeOf(content) === WORKOUT_EXERCISE_MARKER);
    const exerciseByOrder: Record<number, WorkoutExercise> = {};
    workoutRecord?.exercises.forEach((exercise) => {
        exerciseByOrder[exercise.order] = exercise;
    });
    const markerOrders = hasWorkoutMarkers
        ? contents
              .filter((content: any) => contentTypeOf(content) === WORKOUT_EXERCISE_MARKER)
              .map((content: any) => Number(content.content))
        : [];
    const unreferencedExercises =
        workoutRecord && hasWorkoutMarkers
            ? workoutRecord.exercises.filter((exercise) => !markerOrders.includes(exercise.order))
            : [];
    const hasVisibleContents = contents.some((content: any) => {
        const type = contentTypeOf(content);
        return (
            (type === WORKOUT_EXERCISE_MARKER && exerciseByOrder[Number(content.content)]) ||
            (Boolean(content?.content) &&
                ['TEXT', 'IMG', 'IMAGE', 'PICTURE', 'VIDEO', 'FILE'].includes(type))
        );
    });
    const openWorkoutMedia = (mediaList: any[], clickedIndex: number) => {
        const target = mediaList[clickedIndex];
        if (!target?.content) return;
        const opened = window.open(target.content, '_blank', 'noopener,noreferrer');
        if (opened) opened.opener = null;
    };

    return (
        <section style={styles.root} aria-label={`${title} 운동일지 읽기 전용`}>
            <div style={styles.heading}>
                <div style={styles.headingText}>
                    <div style={styles.title}>{title} 운동일지</div>
                    <div style={styles.meta}>
                        {startTime ? startTime.format('YYYY.MM.DD HH:mm') : '-'}
                        {startTime && endTime ? ` ~ ${endTime.format('HH:mm')}` : ''}
                        {writerName ? ` · ${writerName} 강사` : ''}
                    </div>
                </div>
                <span style={styles.readOnlyBadge}>읽기 전용</span>
            </div>

            {workoutRecord && hasWorkoutMarkers && workoutRecord.summaryText && (
                <div style={styles.contentItem}>
                    <WorkoutSummaryBlock text={workoutRecord.summaryText} />
                </div>
            )}

            {contents.map((content: any, index: number) => {
                const type = contentTypeOf(content);
                const exercise =
                    type === WORKOUT_EXERCISE_MARKER
                        ? exerciseByOrder[Number(content.content)]
                        : undefined;

                return (
                    <div
                        key={content?._id || `journal-content-${index}`}
                        style={styles.contentItem}
                        data-journal-content-type={type}
                    >
                        {exercise && hasWorkoutMarkers && (
                            <WorkoutExerciseCard
                                exercise={exercise}
                                displayNumber={exercise.order}
                                onMediaClick={openWorkoutMedia}
                            />
                        )}

                        {type === 'TEXT' && Boolean(content?.content) && (
                            <p style={styles.text}>{content.content}</p>
                        )}

                        {['IMG', 'IMAGE', 'PICTURE'].includes(type) &&
                            Boolean(content?.content) && (
                                <a
                                    href={content.content}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.mediaLink}
                                    aria-label={`${title} 운동 이미지 새 창에서 열기`}
                                >
                                    <img
                                        src={content.content}
                                        alt={`${title} 운동 이미지 ${index + 1}`}
                                        style={styles.media}
                                        loading="lazy"
                                    />
                                </a>
                            )}

                        {type === 'VIDEO' && Boolean(content?.content) && (
                            <a
                                href={content.content}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.videoLink}
                                aria-label={`${title} 운동 영상 새 창에서 열기`}
                            >
                                {content.thumbnail ? (
                                    <>
                                        <img
                                            src={content.thumbnail}
                                            alt={`${title} 운동 영상 미리보기`}
                                            style={styles.videoThumbnail}
                                            loading="lazy"
                                        />
                                        <span style={styles.playButton} aria-hidden="true">
                                            ▶
                                        </span>
                                    </>
                                ) : (
                                    <span style={styles.videoPlaceholder}>영상 열기</span>
                                )}
                            </a>
                        )}

                        {type === 'FILE' && Boolean(content?.content) && (
                            <a
                                href={content.content}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.fileLink}
                                aria-label={`${content.fileName || '첨부파일'} 새 창에서 열기`}
                                title={content.fileName || '첨부파일 열기'}
                            >
                                <span style={styles.fileIcon} aria-hidden="true">
                                    ↓
                                </span>
                                <span style={styles.fileText}>
                                    <strong style={styles.fileLabel}>첨부파일</strong>
                                    <span style={styles.fileName}>
                                        {content.fileName || '파일 열기'}
                                    </span>
                                </span>
                            </a>
                        )}
                    </div>
                );
            })}

            {workoutRecord && hasWorkoutMarkers && unreferencedExercises.length > 0 && (
                <div style={styles.unreferencedExercises}>
                    {unreferencedExercises.map((exercise, index) => (
                        <WorkoutExerciseCard
                            key={`unreferenced-${exercise.order}-${index}`}
                            exercise={exercise}
                            displayNumber={exercise.order}
                            onMediaClick={openWorkoutMedia}
                        />
                    ))}
                </div>
            )}

            {workoutRecord && hasWorkoutMarkers && workoutRecord.closingMessage && (
                <WorkoutClosingBlock text={workoutRecord.closingMessage} />
            )}

            {workoutRecord && !hasWorkoutMarkers && (
                <WorkoutRecordView workoutRecord={workoutRecord} onMediaClick={openWorkoutMedia} />
            )}
            {!workoutRecord && !hasVisibleContents && (
                <div style={styles.empty}>표시할 운동일지 내용이 없습니다.</div>
            )}
        </section>
    );
};

const styles: Record<string, React.CSSProperties> = {
    root: {
        border: '1px solid #DDD6FE',
        borderRadius: 12,
        background: '#FAF8FF',
        padding: 16,
    },
    heading: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 14,
    },
    headingText: { minWidth: 0 },
    title: {
        color: '#4C1D95',
        fontSize: 16,
        lineHeight: '22px',
        fontWeight: 900,
        overflowWrap: 'anywhere',
    },
    meta: { color: '#7C6F9B', fontSize: 13, lineHeight: '19px', fontWeight: 700, marginTop: 4 },
    readOnlyBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#EDE9FE',
        color: '#6D28D9',
        fontSize: 12,
        fontWeight: 850,
        whiteSpace: 'nowrap',
    },
    text: {
        color: '#374151',
        fontSize: 14,
        lineHeight: '22px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        margin: 0,
    },
    contentItem: {
        marginBottom: 12,
    },
    mediaLink: {
        display: 'block',
        width: '100%',
        borderRadius: 9,
        background: '#FFFFFF',
        border: '1px solid #EDE9FE',
        overflow: 'hidden',
        textDecoration: 'none',
    },
    media: { width: '100%', height: 'auto', maxHeight: 520, objectFit: 'contain', display: 'block' },
    videoLink: {
        position: 'relative',
        display: 'flex',
        width: '100%',
        minHeight: 180,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 9,
        background: '#0F172A',
        overflow: 'hidden',
        textDecoration: 'none',
    },
    videoThumbnail: {
        display: 'block',
        width: '100%',
        aspectRatio: '16 / 9',
        objectFit: 'cover',
        opacity: 0.8,
    },
    playButton: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 56,
        height: 56,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.55)',
        color: '#FFFFFF',
        fontSize: 22,
        paddingLeft: 3,
    },
    videoPlaceholder: {
        color: '#FFFFFF',
        fontSize: 13,
        lineHeight: '19px',
        fontWeight: 850,
        overflowWrap: 'anywhere',
        padding: 12,
        textAlign: 'center',
    },
    fileLink: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        maxWidth: 500,
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        background: '#F8FAFC',
        padding: '14px 16px',
        textDecoration: 'none',
        boxSizing: 'border-box',
    },
    fileIcon: {
        display: 'flex',
        flex: '0 0 36px',
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        background: '#EFF6FF',
        color: '#3B82F6',
        fontSize: 20,
        fontWeight: 900,
    },
    fileText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    fileLabel: { color: '#374151', fontSize: 13, fontWeight: 700 },
    fileName: {
        color: '#3B82F6',
        fontSize: 12,
        overflowWrap: 'anywhere',
    },
    unreferencedExercises: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginBottom: 12,
    },
    empty: {
        border: '1px dashed #C4B5FD',
        borderRadius: 10,
        background: '#FAF8FF',
        color: '#7C6F9B',
        padding: '18px 14px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 750,
    },
    error: {
        border: '1px solid #FECACA',
        borderRadius: 10,
        background: '#FEF2F2',
        color: '#B91C1C',
        padding: '18px 14px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 750,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
    },
    retryButton: {
        height: 34,
        border: '1px solid #FCA5A5',
        borderRadius: 8,
        background: '#FFFFFF',
        color: '#B91C1C',
        padding: '0 12px',
        fontSize: 13,
        fontWeight: 850,
        cursor: 'pointer',
    },
};

export default NewOpportunityJournalReadOnly;
